"use client";

import { RefObject, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PortfolioApiQuestion } from "@/type/servicesPageTypes";
import Icon from "@/components/icons/Icons";
import { useIsMobile } from "@/utils/useIsMobile";

interface StepItem {
  id: number;
  title: string;
  questions: PortfolioApiQuestion[];
}

interface Props {
  groupedSteps: StepItem[];
  currentStep: number;
  setCurrentStep: (stepId: number) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export default function TestSectionsSidebar({
  groupedSteps,
  currentStep,
  setCurrentStep,
  scrollContainerRef,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const smallDevice = useIsMobile(883);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const activeStep =
    groupedSteps && groupedSteps.length > 0
      ? groupedSteps.find((item) => item.id === currentStep) || groupedSteps[0]
      : null;

  const completedPercentage =
    groupedSteps && groupedSteps.length > 1
      ? ((currentStep - 1) / (groupedSteps.length - 1)) * 100
      : 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStepChange = (stepId: number) => {
    setCurrentStep(stepId);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }

    setIsOpen(false);
  };

  return (
    <div className="relative md:bg-primaryGreen md:p-5 lg:p-6">
      {/* Desktop Heading */}
      <h2 className="hidden md:block mb-6 px-2 text-2xl font-bold text-white">
        Test Sections
      </h2>

      {!isMounted ||
      !groupedSteps ||
      groupedSteps.length === 0 ||
      !activeStep ? (
        <>
          <div className="relative p-4 pb-0 md:p-0 md:hidden">
            <div className="flex w-full items-center overflow-hidden rounded-full border border-primaryBlue/20 bg-white px-4 py-2.5 xs:py-3.5">
              <div className="relative w-full flex items-center gap-2 overflow-hidden">
                <div className="h-4 w-8 bg-gray-200 rounded-md animate-pulse" />
                <div className="h-4 w-9/12 bg-gray-200 rounded-md animate-pulse" />
              </div>
              <Icon.dropdownDownArrow className="shrink-0" />
            </div>
          </div>

          {/* Desktop Steps List Skeleton */}
          <div className="hidden md:flex flex-col gap-2 md:pxpx-5 py-8 sm:p-6-0">
            {Array.from({ length: 4 }).map((item, index) => {
              const isActive = index === 0;
              return (
                <div
                  key={index}
                  className={`
                  relative flex items-center gap-3 text-left
                  h-16 px-3 rounded-xl transition
                  ${isActive ? "bg-white/15 border border-white/10" : "opacity-60"}
                `}
                >
                  <div className="flex h-7 w-7 lg:h-9 lg:w-9 shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/20 text-2xs lg:text-sm text-white font-medium" />

                  <div className="h-4 w-3/4 bg-white/30 rounded-md animate-pulse" />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div ref={dropdownRef} className="relative p-4 pb-0 md:p-0">
          {/* Trigger */}
          <button
            type="button"
            onClick={() => {
              if (smallDevice) {
                setIsOpen((prev) => !prev);
              }
            }}
            className="flex w-full items-center overflow-hidden rounded-full border border-primaryBlue/20 bg-white md:pointer-events-none md:hidden"
          >
            <div className="relative flex h-full flex-1 items-center gap-2 overflow-hidden px-4 py-2.5 xs:py-3.5">
              <div
                className="absolute inset-y-0 left-0 bg-[#ADFA95] transition"
                style={{
                  width: `${completedPercentage}%`,
                }}
              />

              <div className="relative z-1 flex items-center gap-2">
                <span className="text-sm leading-1.2 text-primaryBlack">
                  <span className="text-[#004ac0]">{currentStep}</span>/
                  {groupedSteps.length}
                </span>

                <span className="truncate text-sm leading-1.2 text-primaryBlack">
                  - {activeStep.title}
                </span>
              </div>
            </div>

            <div className="flex h-full w-12 items-center justify-center bg-white">
              <Icon.dropdownDownArrow
                className={`transition ${isOpen ? "rotate-180" : ""}`}
              />
            </div>
          </button>

          {/* List Wrapper */}
          <AnimatePresence>
            {(isOpen || !smallDevice) && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className={[
                  "absolute left-4 right-4 top-[calc(100%+10px)] z-1 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg",
                  "md:static md:left-auto md:right-auto md:top-auto md:block md:overflow-visible md:rounded-none md:border-none md:bg-transparent md:shadow-none",
                ].join(" ")}
              >
                <div className="relative flex flex-col md:gap-2 p-2 md:px-0">
                  {/* Desktop Active Background */}
                  <motion.div
                    className="absolute inset-x-0 hidden h-16 rounded-xl bg-white md:block"
                    animate={{
                      y:
                        groupedSteps.findIndex(
                          (item) => item.id === currentStep,
                        ) * 72,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 28,
                    }}
                  />

                  {groupedSteps.map((stepItem, index) => {
                    const isActive = stepItem.id === currentStep;
                    const isCompleted = currentStep > stepItem.id;

                    return (
                      <button
                        key={stepItem.id}
                        type="button"
                        onClick={() => {
                          const isPreviousOrCurrent =
                            stepItem.id <= currentStep;

                          if (!isPreviousOrCurrent) return;

                          handleStepChange(stepItem.id);
                        }}
                        className={[
                          "relative flex items-center gap-3 text-left transition w-full rounded-xl border-b border-[#F1F1F1] px-4 py-3 last:border-b-0 md:h-16 md:border-none md:px-3 md:py-0",
                          isActive
                            ? "bg-primaryGreen/10 md:bg-transparent"
                            : "",
                          isActive
                            ? "font-medium"
                            : isCompleted
                              ? "font-semibold"
                              : "opacity-80",
                        ].join(" ")}
                      >
                        <div
                          className={[
                            "flex items-center justify-center h-7 w-7 lg:h-9 lg:w-9 lg:text-sm text-2xs shrink-0 rounded-full border transition",
                            isActive
                              ? "border-primaryGreen bg-primaryGreen text-white"
                              : isCompleted
                                ? "border-primaryGreen bg-primaryGreen text-white md:bg-white md:text-primaryGreen"
                                : "border-gray-700 text-gray-700 md:border-white md:text-white",
                            !isActive && !isCompleted
                              ? "border-gray-300 text-gray-500 md:border-white md:text-white"
                              : "",
                          ].join(" ")}
                        >
                          {isCompleted ? (
                            <Icon.rightSignIcon className="h-3.5 w-3.5 lg:h-5 lg:w-5" />
                          ) : (
                            index + 1
                          )}
                        </div>

                        <span
                          className={`text-2sm leading-1.3 transition md:text-base md:leading-6 ${isActive ? "font-semibold text-primaryGreen" : isCompleted ? "text-primaryGreen md:text-white" : "text-gray-700 md:text-white"}`}
                        >
                          {stepItem.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
